/**
 * useSpeechOutput - Hook for text-to-speech output
 *
 * Provides TTS functionality with Azure Speech SDK (primary)
 * and Web Speech API (fallback).
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk'
import { latexToPlainText } from '@/utils/katexMathPreprocessor'

interface AzureConfig {
  subscriptionKey: string
  region: string
}

// Config from environment variables
const AZURE_CONFIG: AzureConfig = {
  subscriptionKey: import.meta.env.VITE_AZURE_CONFIG_SPEECH_KEY || '',
  region: import.meta.env.VITE_AZURE_CONFIG_SPEECH_REGION || '',
}

const VOICE_NAME = 'en-US-JennyNeural'

interface UseSpeechOutputReturn {
  audioEnabled: boolean
  setAudioEnabled: (enabled: boolean) => void
  isSpeaking: boolean
  speak: (text: string) => Promise<void>
  stop: () => void
  isAvailable: boolean
}

export function useSpeechOutput(): UseSpeechOutputReturn {
  const [audioEnabled, setAudioEnabled] = useState(false)
  const [isSpeaking, setIsSpeaking] = useState(false)
  const synthesizerRef = useRef<SpeechSDK.SpeechSynthesizer | null>(null)
  const playerRef = useRef<SpeechSDK.SpeakerAudioDestination | null>(null)
  const queueRef = useRef<string[]>([])
  const isProcessingRef = useRef(false)

  // Check if Azure Speech is available
  const isAzureAvailable = Boolean(AZURE_CONFIG.subscriptionKey && AZURE_CONFIG.region)
  const isWebSpeechAvailable = typeof window !== 'undefined' && 'speechSynthesis' in window
  const isAvailable = isAzureAvailable || isWebSpeechAvailable

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (synthesizerRef.current) {
        synthesizerRef.current.close()
      }
      if (playerRef.current) {
        playerRef.current.close()
      }
      if (isWebSpeechAvailable) {
        window.speechSynthesis.cancel()
      }
    }
  }, [isWebSpeechAvailable])

  // Process speech queue
  const processQueue = useCallback(async () => {
    if (isProcessingRef.current || !audioEnabled || queueRef.current.length === 0) {
      return
    }

    isProcessingRef.current = true
    setIsSpeaking(true)

    try {
      while (queueRef.current.length > 0 && audioEnabled) {
        const text = queueRef.current.shift()
        if (!text?.trim()) continue

        // Clean LaTeX for speech
        const cleanText = latexToPlainText(text)

        if (isAzureAvailable) {
          await speakWithAzure(cleanText)
        } else if (isWebSpeechAvailable) {
          await speakWithWebSpeech(cleanText)
        }

        // Small pause between items
        await new Promise((r) => setTimeout(r, 250))
      }
    } catch (error) {
      console.error('Speech error:', error)
    } finally {
      isProcessingRef.current = false
      setIsSpeaking(false)
    }
  }, [audioEnabled, isAzureAvailable, isWebSpeechAvailable])

  // Speak with Azure Speech SDK
  const speakWithAzure = useCallback(async (text: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      try {
        const speechConfig = SpeechSDK.SpeechConfig.fromSubscription(
          AZURE_CONFIG.subscriptionKey,
          AZURE_CONFIG.region
        )
        speechConfig.speechSynthesisVoiceName = VOICE_NAME

        const player = new SpeechSDK.SpeakerAudioDestination()
        const audioConfig = SpeechSDK.AudioConfig.fromSpeakerOutput(player)
        const synthesizer = new SpeechSDK.SpeechSynthesizer(speechConfig, audioConfig)

        synthesizerRef.current = synthesizer
        playerRef.current = player

        synthesizer.speakTextAsync(
          text,
          (result) => {
            if (result.reason === SpeechSDK.ResultReason.SynthesizingAudioCompleted) {
              resolve()
            } else {
              reject(new Error(result.errorDetails))
            }
            synthesizer.close()
          },
          (error) => {
            reject(error)
            synthesizer.close()
          }
        )
      } catch (error) {
        reject(error)
      }
    })
  }, [])

  // Speak with Web Speech API (fallback)
  const speakWithWebSpeech = useCallback(async (text: string): Promise<void> => {
    return new Promise((resolve) => {
      const utterance = new SpeechSynthesisUtterance(text)
      utterance.onend = () => resolve()
      utterance.onerror = () => resolve() // Continue on error
      window.speechSynthesis.speak(utterance)

      // Timeout fallback
      setTimeout(resolve, 30000)
    })
  }, [])

  // Public speak function
  const speak = useCallback(async (text: string) => {
    if (!audioEnabled || !text.trim()) return

    queueRef.current.push(text)
    processQueue()
  }, [audioEnabled, processQueue])

  // Stop speaking
  const stop = useCallback(() => {
    queueRef.current = []

    if (synthesizerRef.current) {
      synthesizerRef.current.close()
      synthesizerRef.current = null
    }

    if (playerRef.current) {
      playerRef.current.pause()
      playerRef.current.close()
      playerRef.current = null
    }

    if (isWebSpeechAvailable) {
      window.speechSynthesis.cancel()
    }

    isProcessingRef.current = false
    setIsSpeaking(false)
  }, [isWebSpeechAvailable])

  // Handle audio enabled toggle
  const handleSetAudioEnabled = useCallback((enabled: boolean) => {
    setAudioEnabled(enabled)
    if (!enabled) {
      stop()
    }
  }, [stop])

  return {
    audioEnabled,
    setAudioEnabled: handleSetAudioEnabled,
    isSpeaking,
    speak,
    stop,
    isAvailable,
  }
}

export default useSpeechOutput
